import React, { useEffect, useRef } from 'react';
import { Box, Typography, Container, Divider } from '@mui/material';
import { trackScreenView } from '../lib/gtag.js';

export default function BBFTheSetup() {
  const sectionRef = useRef(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedRef.current && !window.__skipNextTracking) {
          const ua = navigator.userAgent || navigator.vendor || window.opera;
          let platform = 'desktop';
          if (/android/i.test(ua)) platform = 'android';
          else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) platform = 'ios';

          trackScreenView(`BBFTheSetup::${platform}`);
          hasTrackedRef.current = true;
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.5 }
    );

    const node = sectionRef.current;
    if (node) {
      observer.observe(node); // ✅ immediate, reliable GA tracking
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        py: { xs: 6, md: 10 },
        bgcolor: "#4a5a6a",
        color: "#fff",
      }}
    >
      <Container maxWidth="md">
        <Divider sx={{ mb: 6, borderColor: "#999" }} />
        <Typography variant="h4" component="h2" gutterBottom>
          The Setup
        </Typography>

        <Typography variant="body1" paragraph>
          On July 13, 2024, in the small town of Butler, Pennsylvania, Fire Chief Corey Comperatore was murdered, and President Donald J. Trump was wounded, when gunman Thomas Matthew Crooks fired eight shots into the crowd at the Butler County Fairgrounds.
        </Typography>

        <Typography variant="body1" paragraph>
          Trump refused to be rushed away. Bloodied but alive, he raised his fist and shouted:
        </Typography>

        <Typography
          variant="h6"
          component="blockquote"
          sx={{
            fontStyle: "italic",
            my: 2,
            pl: 2,
            borderLeft: "4px solid #f44336",
            color: "#ffeaea",
          }}
        >
          “Fight! Fight! Fight!”
        </Typography>

        <Typography variant="body1" paragraph>
          The Butler Brief takes that very fight — and names those who helped set the stage for murder. Butler was not chosen by accident.
        </Typography>

        <Typography variant="body1" paragraph>
          In 2009, just months after taking office as vice president, Joe Biden’s political network quietly began anchoring itself in Western Pennsylvania. That same year, Rosemont Seneca Partners, LLC was formed — a financial operation that would later become the core of the Biden family’s influence-peddling empire.
        </Typography>

        <Typography variant="body1" paragraph>
          Hunter Biden later bragged that the firm was named after Rosemont Farms — the Heinz family estate, situated just 25 miles south of Butler. That same family later became connected by marriage to John Kerry, the man who would help shield Biden after his now-infamous threat to withhold $1 billion from Ukraine.
        </Typography>

        <Typography variant="body1" paragraph>
          The former owner of that same farm, U.S. Senator John Heinz III, died in a plane crash near Rosemont College, in Rosemont, Pennsylvania, on April 4, 1991 — the 23rd anniversary of the assassination of Dr. Martin Luther King, Jr.
        </Typography>

        <Typography variant="body1" paragraph>
          Except for Rick Santorum, that U.S. Senate seat remained Democratic from Heinz’s death until it was flipped by Dave McCormick in 2024. McCormick was scheduled to be onstage with Trump at the moment of the Butler shooting.
        </Typography>

        <Typography variant="body1" paragraph fontWeight="fontWeightMedium">
          What are the odds?
        </Typography>

        <Typography variant="body1" paragraph fontStyle="italic">
          Were they trying to kill two birds with one stone?
        </Typography>

        <Typography variant="body1" paragraph>
          The Rosemont Seneca Corridor was real — and Butler sits Dead Center.
        </Typography>

        <Typography variant="body1" paragraph fontWeight="bold">
          So why Butler?
        </Typography>
      </Container>
    </Box>
  );
}